/**
 * The planner's map: basemap, the area and its margin, flight lines, photo points, waypoints,
 * takeoff, one photo footprint, a chart-hover marker, and the vertex editor (spec §6.4).
 * Pure geometry lives in `polygonEditor.ts` and `planner/outline.ts`; this file wires events.
 */
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import { Map as MapLibreMap, NavigationControl, ScaleControl, type MapMouseEvent } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Box, Group, SegmentedControl, Slider, Stack, Switch, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { shadeDtm, type Shaded } from '@/dtm/shade';
import { shadedImageUrl } from '@/dtm/shadeImage';
import type { RasterDtm } from '@/dtm/cuzk';
import { useFormat } from '@/components/useFormat';
import { BASEMAP_SOURCE, BASEMAPS, type Basemap } from '@/map/layers';
import { footprintRing } from '@/planner/outline';
import type { LngLat, Plan } from '@/planner/types';
import { MAP, viridis } from '@/theme/colors';
import { brand } from '@/theme/theme';
import { addVertex, deleteVertex, insertVertex, midpoints, moveVertex, type EditorMode } from './polygonEditor';

export interface PlannerMapProps {
  polygon: LngLat[];
  plan: Plan | null;
  takeoff: LngLat | null;
  mode: EditorMode;
  onPolygonChange: (polygon: LngLat[]) => void;
  onTakeoffChange: (p: LngLat) => void;
  onModeChange: (mode: EditorMode) => void;
  selectedPhoto: number | null;
  onSelectPhoto: (index: number | null) => void;
  hoverPoint: LngLat | null;
  marginRing: LngLat[] | null;
  footprint: { across_m: number; along_m: number } | null;
  basemap: Exclude<Basemap, 'none'>;
  onBasemapChange: (b: Exclude<Basemap, 'none'>) => void;
  /** The terrain under the mission, drawn as a shaded overlay when present. */
  dtm: RasterDtm | null;
  terrain: TerrainLayer;
  onTerrainChange: (t: TerrainLayer) => void;
  /** Receives the imperative api once the map exists. */
  onApi?: (api: PlannerMapApi) => void;
}

export interface PlannerMapApi {
  /** Fits the ring, copies the rendered map into a fresh canvas and restores the view. */
  capture(ring: LngLat[]): Promise<HTMLCanvasElement>;
}

export interface TerrainLayer {
  visible: boolean;
  /** 0..1 */
  opacity: number;
}

const DTM_SOURCE = 'dtm';
const DTM_LAYER = 'dtm-raster';

const SRC = ['area', 'margin', 'lines', 'transit', 'photos', 'waypoints', 'takeoff', 'footprint', 'hover', 'vertices', 'midpoints'] as const;
type Src = (typeof SRC)[number];
const PICK_PX = 8;
const EMPTY: FeatureCollection = { type: 'FeatureCollection', features: [] };

function fc(features: Feature<Geometry>[]): FeatureCollection {
  return { type: 'FeatureCollection', features };
}
function point(p: LngLat, props: Record<string, unknown> = {}): Feature<Geometry> {
  return { type: 'Feature', geometry: { type: 'Point', coordinates: p }, properties: props };
}
function polygonFc(ring: LngLat[]): FeatureCollection {
  return ring.length < 3 ? EMPTY : fc([{ type: 'Feature', geometry: { type: 'Polygon', coordinates: [[...ring, ring[0]]] }, properties: {} }]);
}

export function PlannerMap(props: PlannerMapProps) {
  const { t } = useTranslation('missions');
  const { number } = useFormat();
  const container = useRef<HTMLDivElement>(null);
  const shaded = useMemo(() => (props.dtm ? shadeDtm(props.dtm.raster) : null), [props.dtm]);
  const shadedRef = useRef<Shaded | null>(null);
  useLayoutEffect(() => {
    shadedRef.current = shaded;
  }, [shaded]);
  const mapRef = useRef<MapLibreMap | null>(null);
  const propsRef = useRef(props);
  useLayoutEffect(() => {
    propsRef.current = props;
  });
  const drag = useRef<number | null>(null);
  const wasComplete = useRef(isComplete(props.polygon, props.mode));

  // One map for the component's life; data flows through source updates below.
  useEffect(() => {
    if (!container.current) return;
    const basemap = BASEMAPS[propsRef.current.basemap];
    const map = new MapLibreMap({
      container: container.current,
      style: {
        version: 8,
        sources: { [BASEMAP_SOURCE]: { type: 'raster', tiles: [basemap.url], tileSize: 256, maxzoom: 19, attribution: basemap.attribution } },
        layers: [{ id: 'lyr-basemap', type: 'raster', source: BASEMAP_SOURCE }],
      },
      center: [15.5, 49.8],
      zoom: 7,
      attributionControl: {},
    });
    mapRef.current = map;
    propsRef.current.onApi?.({
      capture: (ring) =>
        new Promise((resolve) => {
          const view = { center: map.getCenter(), zoom: map.getZoom(), bearing: map.getBearing(), pitch: map.getPitch() };
          fitTo(map, ring);
          map.once('idle', () => {
            // The WebGL buffer is readable only during a render, so copy from inside one.
            map.once('render', () => {
              const src = map.getCanvas();
              const out = document.createElement('canvas');
              out.width = src.width;
              out.height = src.height;
              out.getContext('2d')?.drawImage(src, 0, 0);
              map.jumpTo(view);
              resolve(out);
            });
            map.triggerRepaint();
          });
        }),
    });
    map.addControl(new NavigationControl({ showCompass: false }), 'top-left');
    map.addControl(new ScaleControl({ unit: 'metric' }), 'bottom-left');
    map.on('load', () => {
      for (const id of SRC) map.addSource(id, { type: 'geojson', data: EMPTY });
      map.addLayer({ id: 'area-fill', type: 'fill', source: 'area', paint: { 'fill-color': MAP.areaFill } });
      map.addLayer({ id: 'area-line', type: 'line', source: 'area', paint: { 'line-color': MAP.areaLine, 'line-width': 2 } });
      map.addLayer({ id: 'margin-line', type: 'line', source: 'margin', paint: { 'line-color': MAP.marginLine, 'line-width': 1.5, 'line-dasharray': [3, 2] } });
      map.addLayer({ id: 'footprint-fill', type: 'fill', source: 'footprint', paint: { 'fill-color': MAP.footprintFill } });
      map.addLayer({ id: 'footprint-line', type: 'line', source: 'footprint', paint: { 'line-color': MAP.footprintLine, 'line-width': 1.5 } });
      map.addLayer({ id: 'transit-line', type: 'line', source: 'transit', paint: { 'line-color': MAP.marginLine, 'line-width': 1.5, 'line-dasharray': [2, 2] } });
      map.addLayer({ id: 'lines-line', type: 'line', source: 'lines', paint: { 'line-color': MAP.flightLine, 'line-width': 1.5 } });
      map.addLayer({ id: 'photos-circle', type: 'circle', source: 'photos', paint: { 'circle-radius': 3, 'circle-color': MAP.missionPhoto, 'circle-stroke-color': MAP.photoOutline, 'circle-stroke-width': 1 } });
      map.addLayer({ id: 'waypoints-circle', type: 'circle', source: 'waypoints', paint: { 'circle-radius': 5, 'circle-color': MAP.missionWaypoint, 'circle-stroke-color': MAP.photoOutline, 'circle-stroke-width': 1 } });
      map.addLayer({ id: 'takeoff-circle', type: 'circle', source: 'takeoff', paint: { 'circle-radius': 8, 'circle-color': MAP.takeoff, 'circle-stroke-color': MAP.photoOutline, 'circle-stroke-width': 2 } });
      map.addLayer({ id: 'hover-circle', type: 'circle', source: 'hover', paint: { 'circle-radius': 7, 'circle-color': MAP.hoverPoint, 'circle-stroke-color': MAP.photoOutline, 'circle-stroke-width': 2 } });
      map.addLayer({ id: 'midpoints-circle', type: 'circle', source: 'midpoints', paint: { 'circle-radius': 4, 'circle-color': MAP.midpoint, 'circle-stroke-color': MAP.vertexOutline, 'circle-stroke-width': 1 } });
      map.addLayer({ id: 'vertices-circle', type: 'circle', source: 'vertices', paint: { 'circle-radius': 6, 'circle-color': MAP.vertex, 'circle-stroke-color': MAP.vertexOutline, 'circle-stroke-width': 2 } });
      syncAll(map, propsRef.current, shadedRef.current);
      if (propsRef.current.polygon.length >= 3) fitTo(map, propsRef.current.polygon);
    });

    const nearest = (p: { x: number; y: number }, candidates: LngLat[]): number | null => {
      let best: number | null = null;
      let bestD = PICK_PX;
      candidates.forEach((c, i) => {
        const q = map.project(c);
        const d = Math.hypot(q.x - p.x, q.y - p.y);
        if (d < bestD) {
          best = i;
          bestD = d;
        }
      });
      return best;
    };

    map.on('click', (e: MapMouseEvent) => {
      const { mode, polygon } = propsRef.current;
      const p: LngLat = [e.lngLat.lng, e.lngLat.lat];
      if (mode === 'takeoff') {
        propsRef.current.onTakeoffChange(p);
        propsRef.current.onModeChange('view');
        return;
      }
      if (mode === 'draw') {
        if (polygon.length >= 3 && nearest(e.point, [polygon[0]]) !== null) {
          propsRef.current.onModeChange('edit');
          return;
        }
        propsRef.current.onPolygonChange(addVertex(polygon, p));
        return;
      }
      if (mode === 'edit') {
        const vi = nearest(e.point, polygon);
        if (vi !== null && e.originalEvent.altKey) {
          propsRef.current.onPolygonChange(deleteVertex(polygon, vi));
          return;
        }
        if (vi !== null) return;
        const mi = nearest(e.point, midpoints(polygon));
        if (mi !== null) {
          propsRef.current.onPolygonChange(insertVertex(polygon, mi, p));
          return;
        }
      }
      const hits = map.queryRenderedFeatures(e.point, { layers: ['photos-circle'] });
      const idx = hits[0]?.properties?.index;
      propsRef.current.onSelectPhoto(typeof idx === 'number' ? idx : null);
    });

    map.on('mousedown', (e: MapMouseEvent) => {
      if (propsRef.current.mode !== 'edit') return;
      const vi = nearest(e.point, propsRef.current.polygon);
      if (vi === null) return;
      drag.current = vi;
      map.dragPan.disable();
      e.preventDefault();
    });
    map.on('mousemove', (e: MapMouseEvent) => {
      if (drag.current === null) return;
      propsRef.current.onPolygonChange(moveVertex(propsRef.current.polygon, drag.current, [e.lngLat.lng, e.lngLat.lat]));
    });
    map.on('mouseup', () => {
      if (drag.current === null) return;
      drag.current = null;
      map.dragPan.enable();
    });
    map.on('dblclick', (e: MapMouseEvent) => {
      const { mode, polygon } = propsRef.current;
      if (mode !== 'draw') return;
      // The two clicks of a double-click each appended a vertex at the same spot.
      const last = polygon[polygon.length - 1];
      const prev = polygon[polygon.length - 2];
      const duplicate =
        prev !== undefined && Math.hypot(map.project(last).x - map.project(prev).x, map.project(last).y - map.project(prev).y) < PICK_PX;
      const trimmed = duplicate ? polygon.slice(0, -1) : polygon;
      if (trimmed.length < 3) return;
      e.preventDefault();
      if (duplicate) propsRef.current.onPolygonChange(trimmed);
      propsRef.current.onModeChange('edit');
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Each source follows only the props it is drawn from, so a hover does not re-send thousands of photos.
  const { polygon, mode, marginRing, plan, takeoff, hoverPoint, selectedPhoto, footprint, terrain } = props;
  useEffect(() => {
    const map = loaded(mapRef.current);
    if (map) syncTerrain(map, shaded, terrain);
  }, [shaded, terrain]);
  useEffect(() => {
    const map = loaded(mapRef.current);
    if (map) syncPolygon(map, polygon, mode);
  }, [polygon, mode]);
  useEffect(() => {
    const map = loaded(mapRef.current);
    if (map) setData(map, 'margin', marginRing ? polygonFc(marginRing) : EMPTY);
  }, [marginRing]);
  useEffect(() => {
    const map = loaded(mapRef.current);
    if (map) syncPlan(map, plan);
  }, [plan]);
  useEffect(() => {
    const map = loaded(mapRef.current);
    if (map) {
      setData(map, 'takeoff', takeoff ? fc([point(takeoff)]) : EMPTY);
      syncTransit(map, plan, takeoff);
    }
  }, [plan, takeoff]);
  useEffect(() => {
    const map = loaded(mapRef.current);
    if (map) setData(map, 'hover', hoverPoint ? fc([point(hoverPoint)]) : EMPTY);
  }, [hoverPoint]);
  useEffect(() => {
    const map = loaded(mapRef.current);
    if (map) syncFootprint(map, plan, selectedPhoto, footprint);
  }, [plan, selectedPhoto, footprint]);

  // An imported polygon or a finished drawing is brought into view; vertex edits keep the view.
  useEffect(() => {
    const complete = isComplete(polygon, mode);
    const map = loaded(mapRef.current);
    if (map && complete && !wasComplete.current) fitTo(map, polygon);
    wasComplete.current = complete;
  }, [polygon, mode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.getCanvas().style.cursor = props.mode === 'view' ? '' : 'crosshair';
    if (props.mode === 'draw') map.doubleClickZoom.disable();
    else map.doubleClickZoom.enable();
  }, [props.mode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    try {
      if (!map.getSource(BASEMAP_SOURCE) || !map.getLayer('area-fill')) return;
      const b = BASEMAPS[props.basemap];
      map.removeLayer('lyr-basemap');
      map.removeSource(BASEMAP_SOURCE);
      map.addSource(BASEMAP_SOURCE, { type: 'raster', tiles: [b.url], tileSize: 256, maxzoom: 19, attribution: b.attribution });
      // The basemap goes back under everything, the terrain overlay included.
      map.addLayer({ id: 'lyr-basemap', type: 'raster', source: BASEMAP_SOURCE }, map.getLayer(DTM_LAYER) ? DTM_LAYER : 'area-fill');
    } catch {
      // Style not loaded yet: the mount effect used the current basemap already.
    }
  }, [props.basemap]);

  return (
    <Box pos="relative" h="100%" style={{ borderRadius: 12, overflow: 'hidden', border: `1px solid ${brand.border}` }}>
      <div ref={container} style={{ position: 'absolute', inset: 0, background: MAP.background }} />
      <Box pos="absolute" top={8} right={8} bg={MAP.controlBg} p={6} style={{ borderRadius: 8, border: `1px solid ${MAP.controlBorder}` }}>
        <Stack gap={6}>
          <SegmentedControl
            size="xs"
            value={props.basemap}
            onChange={(v) => props.onBasemapChange(v as Exclude<Basemap, 'none'>)}
            data={[
              { value: 'osm', label: t('map.osm') },
              { value: 'ortho', label: t('map.ortho') },
            ]}
          />
          {shaded && shaded.min !== null && shaded.max !== null && (
            <>
              <Group gap="xs" wrap="nowrap">
                <Switch size="xs" label={t('map.terrain')} checked={props.terrain.visible} onChange={(e) => props.onTerrainChange({ ...props.terrain, visible: e.currentTarget.checked })} />
                <Slider
                  size="xs"
                  w={72}
                  min={10}
                  max={100}
                  step={5}
                  value={Math.round(props.terrain.opacity * 100)}
                  onChange={(v) => props.onTerrainChange({ ...props.terrain, opacity: v / 100 })}
                  label={(v) => `${v}\u00a0%`}
                  thumbLabel={t('map.terrainOpacity')}
                  disabled={!props.terrain.visible}
                />
              </Group>
              <Group gap={6} wrap="nowrap" aria-label={t('map.terrainLegend')}>
                <Text fz={11} c={brand.textMuted}>
                  {number(shaded.min)}
                </Text>
                <Box h={8} style={{ flex: 1, borderRadius: 4, background: `linear-gradient(90deg, ${[0, 0.25, 0.5, 0.75, 1].map(viridis).join(', ')})` }} />
                <Text fz={11} c={brand.textMuted}>
                  {`${number(shaded.max)}\u00a0${t('map.terrainLegend')}`}
                </Text>
              </Group>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

function setData(map: MapLibreMap, id: Src, data: FeatureCollection) {
  const src = map.getSource(id) as { setData: (d: FeatureCollection) => void } | undefined;
  src?.setData(data);
}

/** The map once its sources exist, else null. */
function loaded(map: MapLibreMap | null): MapLibreMap | null {
  return map && map.getSource('area') ? map : null;
}

function isComplete(polygon: LngLat[], mode: EditorMode): boolean {
  return polygon.length >= 3 && mode !== 'draw';
}

function syncPolygon(map: MapLibreMap, polygon: LngLat[], mode: EditorMode) {
  setData(map, 'area', polygonFc(polygon));
  const editing = mode === 'draw' || mode === 'edit';
  setData(map, 'vertices', editing ? fc(polygon.map((v) => point(v))) : EMPTY);
  setData(map, 'midpoints', mode === 'edit' ? fc(midpoints(polygon).map((v) => point(v))) : EMPTY);
}

function lineString(coordinates: LngLat[]): Feature<Geometry> {
  return { type: 'Feature', geometry: { type: 'LineString', coordinates }, properties: {} };
}

/** The whole flight path in order, so the turns between lines are drawn too. */
function syncPlan(map: MapLibreMap, plan: Plan | null) {
  const path = plan ? plan.waypoints.map((w): LngLat => [w.lng, w.lat]) : [];
  setData(map, 'lines', path.length >= 2 ? fc([lineString(path)]) : EMPTY);
  setData(map, 'photos', plan ? fc(plan.photos.map((ph, i) => point(ph, { index: i }))) : EMPTY);
  setData(map, 'waypoints', plan ? fc(plan.waypoints.map((w) => point([w.lng, w.lat], { height: w.height_m }))) : EMPTY);
}

/** Dashed legs from the takeoff to the first waypoint and from the last one back. */
function syncTransit(map: MapLibreMap, plan: Plan | null, takeoff: LngLat | null) {
  const wps = plan?.waypoints ?? [];
  if (!takeoff || wps.length === 0) {
    setData(map, 'transit', EMPTY);
    return;
  }
  const first = wps[0];
  const last = wps[wps.length - 1];
  setData(map, 'transit', fc([lineString([takeoff, [first.lng, first.lat]]), lineString([[last.lng, last.lat], takeoff])]));
}

/** The shaded raster as an image source under the vectors; rebuilt when the raster changes, restyled when the control moves. */
function syncTerrain(map: MapLibreMap, shaded: Shaded | null, terrain: TerrainLayer) {
  const current = map.getSource(DTM_SOURCE) as { _shaded?: Shaded } | undefined;
  if (current && current._shaded !== shaded) {
    if (map.getLayer(DTM_LAYER)) map.removeLayer(DTM_LAYER);
    map.removeSource(DTM_SOURCE);
  }
  if (!shaded) return;
  if (!map.getSource(DTM_SOURCE)) {
    const url = shadedImageUrl(shaded);
    if (!url) return;
    const { west, south, east, north } = shaded.bounds;
    map.addSource(DTM_SOURCE, {
      type: 'image',
      url,
      coordinates: [
        [west, north],
        [east, north],
        [east, south],
        [west, south],
      ],
    });
    (map.getSource(DTM_SOURCE) as { _shaded?: Shaded })._shaded = shaded;
    map.addLayer({ id: DTM_LAYER, type: 'raster', source: DTM_SOURCE, paint: { 'raster-opacity': terrain.opacity, 'raster-resampling': 'nearest' } }, 'area-fill');
  }
  map.setLayoutProperty(DTM_LAYER, 'visibility', terrain.visible ? 'visible' : 'none');
  map.setPaintProperty(DTM_LAYER, 'raster-opacity', terrain.opacity);
}

function syncFootprint(map: MapLibreMap, plan: Plan | null, selectedPhoto: number | null, fp: PlannerMapProps['footprint']) {
  const sel = selectedPhoto !== null && plan && fp ? plan.photos[selectedPhoto] : null;
  setData(map, 'footprint', sel && plan && fp ? polygonFc(footprintRing(sel, plan.angle_deg, fp.across_m, fp.along_m)) : EMPTY);
}

function syncAll(map: MapLibreMap, p: PlannerMapProps, shaded: Shaded | null) {
  syncTerrain(map, shaded, p.terrain);
  syncPolygon(map, p.polygon, p.mode);
  setData(map, 'margin', p.marginRing ? polygonFc(p.marginRing) : EMPTY);
  syncPlan(map, p.plan);
  setData(map, 'takeoff', p.takeoff ? fc([point(p.takeoff)]) : EMPTY);
  syncTransit(map, p.plan, p.takeoff);
  setData(map, 'hover', p.hoverPoint ? fc([point(p.hoverPoint)]) : EMPTY);
  syncFootprint(map, p.plan, p.selectedPhoto, p.footprint);
}

function fitTo(map: MapLibreMap, ring: LngLat[]) {
  const lngs = ring.map((p) => p[0]);
  const lats = ring.map((p) => p[1]);
  map.fitBounds(
    [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ],
    { padding: 60, duration: 0 },
  );
}
